import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { GridConfig } from '@inavista/components/grid';

// Service Order popup and ISC popup → History tab → Service Order and Equipment grids on @inavista/components. Same
// columns, labels, widths and toolbar as the Syncfusion grids they replace. Rows: the order's own `history` array and
// the existing equipment history call (LocalRowsGridApi, no new endpoint). No paging: the grid scrolls, in the old
// grid's 10-row page (history-tab.component.scss).

export const SO_HISTORY_URL = 'page://so-history';
export const EQUIPMENT_HISTORY_URL = 'page://so-equipment-history';
export const SO_HISTORY_PERSISTENCE_KEY = 'rims.service.so-popup-history';
export const EQUIPMENT_HISTORY_PERSISTENCE_KEY = 'rims.service.so-popup-equipment-history';

export const SO_HISTORY_COLUMNS: [string, string][] = [
  ['createdByName', 'nvarchar'],
  ['text', 'nvarchar'],
  ['createdDate', 'datetime'],
];

export const EQUIPMENT_HISTORY_COLUMNS: [string, string][] = [
  ['serviceOrderId', 'nvarchar'],
  ['technicianName', 'nvarchar'],
  ['createdDate', 'datetime'],
  ['callGroup', 'nvarchar'],
  ['totalAmount', 'decimal'],
];

const LEFT_HEADER = { header: { horizontal: 'left' as const } };

// What both grids share: the old toolbar was Columns, Search, Excel export (role AllGrid_Toolbar_Excel_Export),
// Clear filters. Reset layout is kept: it undoes the saved layout.
function base(canExport: boolean) {
  return {
    clientSide: true,
    // A synthetic key (the rows have none); not a column.
    keyColumn: 'rowId',
    // 30, as the other Service Order popup grids.
    rowHeight: 30,
    enableExport: canExport,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    // The old Show/Hide Columns button did nothing (no template, no handler): no Columns picker either.
    enableColumnsPicker: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
  } satisfies Partial<GridConfig>;
}

export function buildSoHistoryGridConfig(canExport: boolean): GridConfig {
  return {
    ...base(canExport),
    apiBaseUrl: SO_HISTORY_URL,
    defaultColumnOrder: ['createdByName', 'text', 'createdDate'],
    columnLabels: { createdByName: 'Updated By', text: 'Description', createdDate: 'Created Date' },
    // Updated By 200 and Created Date 180 as before; Description had no width and took the rest.
    columnWidths: { createdByName: 200, text: 300, createdDate: 180 },
    columnSizing: { createdByName: { noStretch: true }, createdDate: { noStretch: true } },
    columnAlign: { createdByName: LEFT_HEADER, text: LEFT_HEADER, createdDate: LEFT_HEADER },
    columnTypes: { createdDate: 'datetime' },
    // The old format, 'hh' (12-hour clock, no AM/PM) included.
    columnDateFormats: { createdDate: 'd MMM, y hh:mm' },
    excelFilterColumns: ['createdByName'],
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: SO_HISTORY_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}

export function buildEquipmentHistoryGridConfig(canExport: boolean): GridConfig {
  return {
    ...base(canExport),
    apiBaseUrl: EQUIPMENT_HISTORY_URL,
    defaultColumnOrder: ['serviceOrderId', 'technicianName', 'createdDate', 'callGroup', 'totalAmount'],
    // The old grid kept Order No. out of the column chooser.
    alwaysVisibleColumns: ['serviceOrderId'],
    columnLabels: {
      serviceOrderId: 'Order No.', technicianName: 'Technician Name', createdDate: 'Start Date',
      callGroup: 'Call Group', totalAmount: 'Total Amount',
    },
    // As before; Call Group had no width and took the rest.
    columnWidths: { serviceOrderId: 120, technicianName: 180, createdDate: 140, callGroup: 150, totalAmount: 200 },
    columnSizing: {
      serviceOrderId: { noStretch: true }, technicianName: { noStretch: true }, createdDate: { noStretch: true },
      totalAmount: { noStretch: true },
    },
    columnAlign: {
      serviceOrderId: { ...LEFT_HEADER },
      technicianName: LEFT_HEADER,
      createdDate: LEFT_HEADER,
      callGroup: LEFT_HEADER,
      totalAmount: { ...LEFT_HEADER, horizontal: 'right' },
    },
    columnTypes: { createdDate: 'date', totalAmount: 'decimal' },
    columnDateFormats: { createdDate: 'd MMM, y' },
    // The old column had no format: the number as it comes, no thousands separator.
    columnNumberFormats: { totalAmount: { useGrouping: false, maximumFractionDigits: 20 } },
    hyperlinkColumns: ['serviceOrderId'],
    excelFilterColumns: ['technicianName', 'callGroup', 'totalAmount'],
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: EQUIPMENT_HISTORY_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
